import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Paperclip, X, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../utils/imageCompression';
import { ImageWithFallback } from './ui/image-with-fallback';

/**
 * Reusable evidence-image uploader used in Support Tickets (member + admin).
 *
 * Props:
 *   api            axios instance from useAuth()
 *   value          string[] of uploaded image URLs
 *   onChange       (urls: string[]) => void
 *   max            max images allowed (default 4)
 *   testIdPrefix   prefix for data-testids
 *
 * Compresses each image (maxSide 1600, q 0.82) before uploading via
 * the existing /api/upload/member-file endpoint.
 */
export function AttachmentUploader({
  api,
  value = [],
  onChange,
  max = 4,
  testIdPrefix = 'attach',
  purpose = 'attachment',
  subjectName = '',
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - (value?.length || 0);
    if (remaining <= 0) {
      toast.error(`You can attach up to ${max} images`);
      return;
    }
    const toUpload = files.slice(0, remaining);

    setUploading(true);
    const uploaded = [];
    for (const f of toUpload) {
      if (!f.type?.startsWith('image/')) {
        toast.error(`Skipped non-image file: ${f.name}`);
        continue;
      }
      try {
        const before = f.size;
        const compressed = await compressImage(f, { maxSide: 1600, quality: 0.82 });
        if (compressed.size < before) {
          toast.message(`Compressed ${f.name}: ${formatCompressionStats(before, compressed.size)}`);
        }
        const fd = new FormData();
        fd.append('file', compressed);
        fd.append('purpose', purpose || 'attachment');
        if (subjectName) fd.append('subject_name', subjectName);
        const up = await api.post('/api/upload/member-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = up.data.url || up.data.file_url;
        if (url) uploaded.push(url);
      } catch (err) {
        toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (uploaded.length) onChange([...(value || []), ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    onChange((value || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={pick}
          disabled={uploading || (value?.length || 0) >= max}
          className="border-white/20 text-[#A0B4CC] hover:bg-white/5 h-8"
          data-testid={`${testIdPrefix}-btn`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Attach image
            </>
          )}
        </Button>
        <span className="text-[10px] text-[#5C7599]">
          {(value?.length || 0)}/{max} · JPG/PNG · auto-compressed
        </span>
      </div>

      {(value?.length || 0) > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className="relative group w-16 h-16 rounded-lg overflow-hidden border border-white/10"
              data-testid={`${testIdPrefix}-thumb-${i}`}
            >
              <ImageWithFallback
                src={url}
                alt={`Attachment ${i + 1}`}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-[#000B18] flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-[#5C7599]" />
                  </div>
                }
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                aria-label="Remove"
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Display-only gallery for attachments inside a ticket message bubble.
 *   <AttachmentGallery attachments={msg.attachments} />
 */
export function AttachmentGallery({ attachments, onOpen }) {
  if (!attachments || !attachments.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="attachment-gallery">
      {attachments.map((url, i) => (
        <button
          key={`${url}-${i}`}
          type="button"
          onClick={() => onOpen?.(url, i, attachments)}
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border border-white/10 hover:border-[#00E5FF]/40 transition-colors"
          data-testid={`attachment-${i}`}
        >
          {url ? (
            <ImageWithFallback
              src={url}
              alt={`Evidence ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
              fallback={<ImageIcon className="w-5 h-5 text-[#5C7599] m-auto" />}
            />
          ) : (
            <ImageIcon className="w-5 h-5 text-[#5C7599] m-auto" />
          )}
        </button>
      ))}
    </div>
  );
}
